import * as React from 'react'

import {
  Avatar,
  Box,
  Button,
  CardInfo,
  Grid,
  Icon,
  Text,
  Title,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendToUI, dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import users, { employeeUser } from 'modules/core/store/collections/users'

import ApprovalsInformationBlock from 'modules/approvals/components/ApprovalsInformationBlock'
import useContractVersionUpdates from 'modules/contracts//hooks/useContractVersionUpdates'
import { FLOWS } from 'modules/contracts/components/FlowSelectorModal/constants'
import { contractVersionEmployee } from 'modules/contracts/lib/contractVersionEmployee'
import type ContractVersionRequest from 'modules/contracts/store/models/ContractVersionRequest'
import employees from 'modules/employees/store/collections/employees'

import CardHeader from '../_CardHeader'
import CardRow from '../_CardRow'
import ConfirmDeleteRequestModal from './_ConfirmDeleteRequestModal'
import ConfirmRejectModal from './_ConfirmRejectModal'
import * as styles from './index.vanilla.css'

type Props = {
  contractVersionRequest: ContractVersionRequest
  rejectRequest: (reason?: string) => void
  approveRequest: () => void
  deleteRequest: () => void
}

const attributesBlackList = new Set([
  'approvers_ids',
  'approval_author_id',
  'approval_request_created_at',
  'approval_action_type',
  'request_details',
])

const Content = ({
  contractVersionRequest,
  rejectRequest,
  approveRequest,
  deleteRequest,
}: Props) => {
  const employee = contractVersionEmployee(contractVersionRequest, employees)

  const {
    access: { id: currentAccessId },
  } = useCurrentSession()
  const requesterId = contractVersionRequest.attributes.approval_author_id
  const requester = users.get(requesterId)
  const requestedAt = contractVersionRequest.attributes.approval_request_created_at || ''
  const requestEffectiveOnDate = contractVersionRequest?.effectiveOn
  const approvers_ids = contractVersionRequest.attributes.approvers_ids || []
  const canAcceptOrReject = approvers_ids.includes(currentAccessId)
  const canDeleteRequest = currentAccessId == requesterId
  const requesterName = requester?.fullName() || ''
  const employeeName = employee ? employeeUser(employee).fullName() : ''

  const { updates } = useContractVersionUpdates(contractVersionRequest, {
    withCustomFieldsValues: true,
  })
  const filteredUpdates = updates.filter((update) => !attributesBlackList.has(update.key))
  const [showConfirmRejectModal, setShowConfirmRejectModal] = React.useState(false)
  const [showConfirmDeleteRequestModal, setShowConfirmDeleteRequestModal] = React.useState(false)

  const confirmReject = (reason?: string) => {
    rejectRequest(reason)
    setShowConfirmRejectModal(false)
  }
  const onClickReject = () => {
    setShowConfirmRejectModal(true)
  }

  const confirmDeleteRequest = () => {
    deleteRequest()
    setShowConfirmDeleteRequestModal(false)
  }

  const onClickDelete = () => {
    setShowConfirmDeleteRequestModal(true)
  }

  const requestDetails = contractVersionRequest.attributes.request_details
  const actionType = contractVersionRequest.attributes.approval_action_type || ''

  let title = i18n.t('contract_versions.approval_page.promotion.title', {
    requesterName,
    employeeName,
  })
  let reasonTitle = i18n.t('contract_versions.approval_page.promotion.reason_title')

  if (actionType === FLOWS.evolution) {
    title = i18n.t('contract_versions.approval_page.evolution.title', {
      requesterName,
      employeeName,
    })
    reasonTitle = i18n.t('contract_versions.approval_page.evolution.reason_title')
  }

  return (
    <Box width="full">
      <Grid variant="fixed">
        <Box gridColSpan={{ xxs: 12, xs: 8, s: 6 }} gap="s12">
          <Avatar size="s24" url={buildUrl(requester?.avatar)} label={requesterName} />
          <Title title={title} />
          <Box flexDirection="row" alignItems="center" gap="s8">
            <Icon.Small icon="clock" />
            <Text size="200">
              {i18n.t('contract_versions.approval_page.date_label', {
                date: dateBackendToUI(requestedAt),
              })}
            </Text>
          </Box>
          {requestDetails && (
            <>
              <Box paddingTop="s16">
                <Text size="100" caps weight="semibold">
                  {reasonTitle}
                </Text>
              </Box>
              <Text size="200">{requestDetails}</Text>
            </>
          )}
          {canDeleteRequest && (
            <Box paddingTop="s16">
              <Button type="secondary" onClick={() => onClickDelete()} iconName="delete">
                {i18n.t('contract_versions.approval_page.delete_request')}
              </Button>
            </Box>
          )}
          <Box gap="s64">
            <Box paddingTop="s32">
              <ApprovalsInformationBlock
                resourceId={contractVersionRequest.id}
                resourceType="Contracts::ContractVersion"
              />
            </Box>
            <HelpCenterLink name="about-contracts-approval-system">
              {i18n.t('contract_versions.approval_page.help_center')}
            </HelpCenterLink>
          </Box>
        </Box>
        <Box
          gridColSpan={{ xxs: 12, xs: 4, s: 6 }}
          justifyContent="flexStart"
          alignItems="flexStart"
          width="full"
        >
          <CardInfo
            header={
              <CardHeader
                canApproveOrReject={canAcceptOrReject}
                requestStatus={contractVersionRequest.attributes.status}
                actionType={actionType}
                onClickApprove={approveRequest}
                onClickReject={onClickReject}
              />
            }
          >
            <Box gap="s12">
              <Text size="200">
                {i18n.t('contract_versions.approval_page.promotion.card_info.effective_date')}
              </Text>
              <Box className={styles.field} flexDirection="row" gap="s12">
                <Box padding="s12">{dateToString(requestEffectiveOnDate, 'ui')}</Box>
              </Box>
              {filteredUpdates.map((update, index) => (
                <CardRow key={update.key + '-' + index} update={update}></CardRow>
              ))}
            </Box>
          </CardInfo>
        </Box>
      </Grid>
      {showConfirmRejectModal && (
        <ConfirmRejectModal
          onClose={() => setShowConfirmRejectModal(false)}
          onSubmit={(reason) => confirmReject(reason)}
          authorFullName={requesterName}
          targetFullName={employeeName}
        />
      )}
      {showConfirmDeleteRequestModal && (
        <ConfirmDeleteRequestModal
          onClose={() => setShowConfirmDeleteRequestModal(false)}
          onSubmit={() => confirmDeleteRequest()}
          targetFullName={employeeName}
          actionType={actionType}
        />
      )}
    </Box>
  )
}

export default observer(Content)
